export default function StatsSection() {
  const topStats = [
    {
      value: "1M+",
      label: "Calls Performed",
      icon: "ri-phone-line",
      color: "text-primary",
      bg: "bg-orange-50",
    },
    {
      value: "180k+",
      label: "Calls Per Day",
      icon: "ri-speed-up-line",
      color: "text-secondary",
      bg: "bg-blue-50",
    },
    {
      value: "&lt;4%",
      label: "Hang Up in First 5–10 Seconds",
      icon: "ri-close-circle-line",
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      value: "80–95%",
      label: "of Leads Come From AI Calls",
      icon: "ri-robot-line",
      color: "text-primary",
      bg: "bg-orange-50",
    },
  ];

  return (
    <section className="bg-white py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-4 md:mb-6">
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-bar-chart-line text-primary text-xs" />
            <span className="text-xs font-semibold text-primary">Platform Performance</span>
          </div>
          {/* Mobile heading */}
          <h2
            className="block md:hidden text-navy font-semibold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            What This Means for You
          </h2>
          {/* Desktop heading */}
          <h2
            className="hidden md:block text-navy font-semibold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            What This Means for Your Title Company
          </h2>

          {/* Mobile copy — simplified */}
          <div className="block md:hidden text-center">
            <p className="text-navy font-bold text-base mb-2" style={{ lineHeight: "1.65" }}>
              8–10 additional closings covers the cost
            </p>
            <p className="text-[#4a5568] text-base" style={{ lineHeight: "1.65" }}>
              Everything beyond that is incremental revenue.
            </p>
          </div>

          {/* Desktop copy — original */}
          <div className="hidden md:block">
            <p className="text-[#4a5568] text-base mb-3" style={{ lineHeight: "1.65" }}>
              If your market sees 2,000–5,000 listings per month, Imperium ensures you&apos;re in front of agents at
              the exact moment closings are forming — not after they&apos;ve already chosen a title partner.
            </p>
            <p className="text-navy font-bold text-base" style={{ lineHeight: "1.65" }}>
              Just 8–10 additional closings per month typically covers the full investment — everything beyond that is incremental revenue.
            </p>
          </div>
        </div>

        {/* Top 4 stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 mt-12">
          <div className="col-span-2 lg:col-span-4 flex items-center gap-2 mb-2">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="block md:hidden text-xs font-semibold text-[#4a5568] tracking-widest uppercase whitespace-nowrap">
              Proven at Scale
            </span>
            <span className="hidden md:block text-xs font-semibold text-[#4a5568] tracking-widest uppercase whitespace-nowrap">
              Proven at Scale Across Millions of AI Conversations
            </span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          {topStats.map((stat) => (
            <div key={stat.label} className="bg-alt-bg rounded-2xl p-6 border border-gray-100 text-center">
              <div className={`w-11 h-11 flex items-center justify-center ${stat.bg} rounded-xl mx-auto mb-4`}>
                <i className={`${stat.icon} ${stat.color} text-xl`} />
              </div>
              <p
                className="text-navy font-extrabold mb-1"
                style={{ fontSize: "clamp(24px, 2.5vw, 36px)" }}
                dangerouslySetInnerHTML={{ __html: stat.value }}
              />
              <p className="text-[#4a5568] text-xs leading-tight">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Two-column layout: Title stat + Voicemail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Title & Mortgage stat + trust cards */}
          <div className="lg:col-span-2 flex flex-col gap-5">

            {/* ── MOBILE heading ── */}
            <div className="block md:hidden">
              {/* Kicker */}
              <p className="text-[#4a5568] text-sm italic mb-3 leading-relaxed">
                Most title companies don&apos;t lose because they&apos;re bad — they lose because they&apos;re late.
              </p>
              <h3 className="text-navy font-semibold text-lg mb-4">
                Winning Comes Down to Timing
              </h3>
              {/* Intro block replacing the gray industry card */}
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-5 mb-1">
                <p className="text-navy text-sm leading-relaxed">
                  In title, winning isn&apos;t about effort — it&apos;s about timing.{" "}
                  <strong>Imperium puts you in the conversation first.</strong>
                </p>
              </div>
            </div>

            {/* ── DESKTOP heading ── */}
            <h3 className="hidden md:block text-navy font-semibold text-lg">
              Title &amp; Mortgage — Where Timing Wins
            </h3>

            {/* Single industry card — desktop only */}
            <div className="hidden md:block bg-alt-bg rounded-xl p-5 border border-gray-100">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <p className="text-navy font-semibold text-sm">Title &amp; Mortgage (Imperium&apos;s Core)</p>
                  <p className="text-[#4a5568] text-xs mt-1 leading-relaxed">
                    In title, winning is about timing and trust — not volume. Imperium gets you into the
                    conversation first, before agents have already committed to a title partner.
                  </p>
                </div>
                <span className="text-primary font-bold text-sm whitespace-nowrap flex-shrink-0">
                  Relationship-Driven
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: "72%" }} />
              </div>
            </div>

            {/* Trust callout */}
            <div className="bg-navy rounded-xl p-6 text-white flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-white text-xl" />
              </div>
              <div>
                <p className="text-white font-bold text-sm mb-1">Built for Title — Not Adapted From It</p>
                <p className="text-white/75 text-sm leading-relaxed">
                  Designed specifically for title companies based on real transaction workflows. Every call
                  sequence, timing trigger, and agent touchpoint reflects how closings actually happen in your
                  market — not a generic sales cadence repurposed for real estate.
                </p>
              </div>
            </div>

            {/* ROI callout */}
            <div className="bg-alt-bg rounded-xl p-5 border border-gray-100 flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0">
                <i className="ri-funds-line text-primary text-base" />
              </div>
              <div>
                <p className="text-navy font-semibold text-sm mb-1">Investment That Pays for Itself</p>
                {/* Mobile copy */}
                <p className="block md:hidden text-[#4a5568] text-xs leading-relaxed">
                  Most partners cover their cost with just 8–10 additional closings.{" "}
                  <strong className="text-navy">Everything after that is pure revenue.</strong>
                </p>
                {/* Desktop copy */}
                <p className="hidden md:block text-[#4a5568] text-xs leading-relaxed">
                  Most title partners cover their full monthly investment within the first 8–10 additional
                  closings. Every closing after that is{" "}
                  <strong className="text-navy">pure incremental revenue</strong> — month after month.
                </p>
              </div>
            </div>
          </div>

          {/* Voicemail + Lead breakdown */}
          <div className="flex flex-col gap-4">
            {/* Voicemail highlight */}
            <div className="bg-navy rounded-2xl p-6 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl mb-4">
                <i className="ri-voiceprint-line text-white text-xl" />
              </div>
              <h3 className="font-bold text-base mb-2">Even When You Miss the Call, You Stay in the Deal</h3>

              {/* Mobile description — shortened */}
              <div className="block md:hidden">
                <p className="text-white/70 text-sm leading-relaxed mb-5">
                  Every missed call still creates a touchpoint.{" "}
                  Imperium leaves a professional voicemail — so you stay in the conversation.
                </p>
              </div>

              {/* Desktop description — original */}
              <div className="hidden md:block">
                <p className="text-white/70 text-sm leading-relaxed mb-3">
                  Every unanswered call still creates a touchpoint with the agent — extending your reach across
                  the entire market.
                </p>
                <p className="text-white/70 text-sm leading-relaxed mb-5">
                  Imperium automatically leaves a professional, personalized voicemail so no opportunity goes
                  silent.
                </p>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60 text-xs">Live Calls → Leads</span>
                  <span className="text-white font-bold text-sm">80–95%</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                  <div className="h-full bg-primary rounded-full" style={{ width: "87%" }} />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60 text-xs">Voicemails → Leads</span>
                  <span className="text-white font-bold text-sm">10–20%</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-secondary rounded-full" style={{ width: "15%" }} />
                </div>
              </div>
            </div>

            {/* ── MOBILE: merged High Engagement card ── */}
            <div className="block md:hidden bg-alt-bg rounded-2xl p-5 border border-gray-100 flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0">
                <i className="ri-signal-wifi-line text-primary text-base" />
              </div>
              <div>
                <p className="text-navy font-semibold text-sm mb-1">High Engagement Conversations</p>
                <p className="text-[#4a5568] text-xs leading-relaxed">
                  Most agents stay on the call long enough to hear your value.{" "}
                  Every touchpoint — live or voicemail — keeps you top of mind.
                </p>
              </div>
            </div>

            {/* ── DESKTOP: original two separate cards ── */}
            <div className="hidden md:flex flex-col gap-4">
              <div className="bg-alt-bg rounded-2xl p-5 border border-gray-100 flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0">
                  <i className="ri-lightbulb-line text-primary text-base" />
                </div>
                <div>
                  <p className="text-navy font-semibold text-sm mb-1">Every Touch Point Counts</p>
                  <p className="text-[#4a5568] text-xs leading-relaxed">
                    The combination of live AI conversations <strong className="text-navy">plus</strong> automatic
                    voicemails means you&apos;re reaching more agents and staying present across the entire market
                    with zero extra effort.
                  </p>
                </div>
              </div>

              <div className="bg-alt-bg rounded-2xl p-5 border border-gray-100 flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-50 rounded-lg flex-shrink-0">
                  <i className="ri-drop-line text-secondary text-base" />
                </div>
                <div>
                  <p className="text-navy font-semibold text-sm mb-1">Under 4% Immediate Hang-Ups</p>
                  <p className="text-[#4a5568] text-xs leading-relaxed">
                    Less than 4% of recipients hang up in the first 5–10 seconds. The vast majority stay on the
                    call long enough to hear your value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
